/**
 * API client for backend communication
 */

import {
  AuthResponse,
  LeaderboardResponse,
  LeaderboardPeriod,
  ScoreSubmission,
  ScoreSubmissionResponse,
  UserProfile,
  ApiError
} from '../types/api';

// Use direct backend URL in development to avoid Vite proxy issues with POST bodies
const API_BASE_URL = import.meta.env.DEV ? 'http://localhost:3001/api' : '/api';

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const fetchOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  };

  console.log('[API] Request:', {
    url,
    method: options.method || 'GET',
    headers: fetchOptions.headers,
    body: options.body
  });

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      error: 'Unknown error',
      message: `HTTP ${response.status}: ${response.statusText}`
    }));
    throw new Error(error.message || error.error);
  }

  return response.json();
}

/**
 * Get token from localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem('snap-pairs-token');
}

/**
 * Save token to localStorage
 */
export function saveToken(token: string): void {
  localStorage.setItem('snap-pairs-token', token);
}

/**
 * Remove token from localStorage
 */
export function clearToken(): void {
  localStorage.removeItem('snap-pairs-token');
}

/**
 * Auth API
 */
export const authAPI = {
  /**
   * Exchange Google ID token for JWT
   */
  async googleSignIn(idToken: string): Promise<AuthResponse> {
    const response = await fetchAPI<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken })
    });

    // Save token to localStorage
    saveToken(response.token);

    return response;
  },

  /**
   * Get current user profile
   */
  async getProfile(): Promise<UserProfile> {
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    return fetchAPI<UserProfile>('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  },

  /**
   * Sign out (clear token)
   */
  signOut(): void {
    clearToken();
  }
};

/**
 * Leaderboard API
 */
export const leaderboardAPI = {
  /**
   * Get leaderboard for a specific period
   */
  async getLeaderboard(
    period: LeaderboardPeriod,
    limit: number = 100,
    gridSize: 4 | 6 | 8 = 6
  ): Promise<LeaderboardResponse> {
    const token = getToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return fetchAPI<LeaderboardResponse>(
      `/leaderboard/${period}?limit=${limit}&gridSize=${gridSize}`,
      { headers }
    );
  },

  /**
   * Submit a score
   */
  async submitScore(
    submission: ScoreSubmission
  ): Promise<ScoreSubmissionResponse> {
    const token = getToken();
    if (!token) {
      throw new Error('Must be authenticated to submit scores');
    }

    return fetchAPI<ScoreSubmissionResponse>('/leaderboard/submit', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(submission)
    });
  }
};
